<template>
    <div class="bg-white rounded border border-gray-200 h-full flex flex-col">
        <!-- Header -->
        <div class="border-b border-gray-200 p-4">
            <div class="flex items-center justify-between">
                <h3 class="font-semibold text-gray-900">Conversations</h3>
                <Button
                    size="sm"
                    @click="$emit('new-conversation')"
                >
                    <PlusIcon class="h-4 w-4 mr-1" />
                    New
                </Button>
            </div>
        </div>

        <!-- Conversations List -->
        <div ref="listContainer" class="flex-1 overflow-y-auto p-2" @scroll="handleScroll">
            <!-- Loading State -->
            <div v-if="isLoading" class="flex items-center justify-center py-8">
                <Loader2Icon class="h-6 w-6 animate-spin text-gray-400" />
            </div>

            <!-- Empty State -->
            <div v-else-if="conversations.length === 0" class="text-center py-8">
                <MessageCircleIcon class="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p class="text-gray-500 text-sm">No conversations yet</p>
                <button
                    class="mt-2 text-blue-500 hover:text-blue-600 text-sm"
                    @click="$emit('new-conversation')"
                >
                    Start your first chat
                </button>
            </div>

            <!-- Conversation Items -->
            <div v-else>
                <div class="space-y-1">
                    <div
                        v-for="conversation in conversations"
                        :key="conversation._id"
                        :class="[
                            'group relative rounded-lg p-3 cursor-pointer transition-colors',
                            currentConversationId === conversation._id
                                ? 'bg-blue-50 border border-blue-200'
                                : 'hover:bg-gray-50 border border-transparent'
                        ]"
                        @click="$emit('select-conversation', conversation._id)"
                    >
                        <!-- Conversation Title -->
                        <div class="flex items-start justify-between">
                            <div class="flex-1 min-w-0">
                                <h4
                                    :class="[
                                        'text-sm font-medium truncate',
                                        currentConversationId === conversation._id
                                            ? 'text-blue-900'
                                            : 'text-gray-900'
                                    ]"
                                >
                                    {{ conversation.title }}
                                </h4>
                                <div class="flex items-center space-x-2 mt-1">
                                    <span class="text-xs text-gray-500">
                                        {{ formatDate(conversation.updated_at) }}
                                    </span>
                                    <span
                                        v-if="conversation.message_count > 0"
                                        class="text-xs text-gray-400"
                                    >
                                        {{ conversation.message_count }} messages
                                    </span>
                                </div>
                                <!-- DEBUG: Show operator and organization info -->
                                <div v-if="conversation.operator_id" class="flex items-center space-x-2 mt-1">
                                    <span class="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                                        {{ conversation.operator ?
                                            `${conversation.operator.first_name} ${conversation.operator.last_name}`.trim() || conversation.operator.email :
                                            `Op: ${conversation.operator_id?.substring(0, 8) || 'Unknown'}`
                                        }}
                                    </span>
                                </div>
                            </div>

                            <!-- Actions Dropdown -->
                            <div class="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <DropdownMenu>
                                    <DropdownMenuTrigger as-child>
                                        <button
                                            class="p-1 rounded hover:bg-gray-200"
                                            @click.stop
                                        >
                                            <MoreVerticalIcon class="h-4 w-4 text-gray-400" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem @click="handleRename(conversation)">
                                            <EditIcon class="h-4 w-4 mr-2" />
                                            Rename
                                        </DropdownMenuItem>
                                        <DropdownMenuItem class="text-red-600" @click="handleDelete(conversation)">
                                            <TrashIcon class="h-4 w-4 mr-2" />
                                            Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>

                        <!-- Preview of first message if available -->
                        <div
                            v-if="conversation.messages && conversation.messages.length > 0"
                            class="mt-2 text-xs text-gray-500 truncate"
                        >
                            {{ getMessagePreview(conversation.messages[0]) }}
                        </div>
                    </div>
                </div>

                <div v-if="hasMore || isLoadingMore" class="flex items-center justify-center py-3 text-xs text-gray-400">
                    <Loader2Icon v-if="isLoadingMore" class="mr-2 h-4 w-4 animate-spin text-gray-400" />
                    <span>{{ isLoadingMore ? 'Loading more...' : 'Scroll to load more' }}</span>
                </div>
            </div>
        </div>


        <!-- Rename Modal -->
        <Dialog :open="isRenameModalOpen" @update:open="isRenameModalOpen = $event">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Rename Conversation</DialogTitle>
                </DialogHeader>
                <div class="space-y-4">
                    <input
                        v-model="newTitle"
                        type="text"
                        class="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                        placeholder="Enter new title..."
                        @keydown.enter="confirmRename"
                    />
                    <div class="flex justify-end space-x-2">
                        <Button
                            variant="outline"
                            @click="isRenameModalOpen = false"
                        >
                            Cancel
                        </Button>
                        <Button
                            :disabled="!newTitle.trim()"
                            @click="confirmRename"
                        >
                            Rename
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        <!-- Delete Confirmation Modal -->
        <Dialog :open="isDeleteModalOpen" @update:open="isDeleteModalOpen = $event">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete Conversation</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this conversation? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <div class="flex justify-end space-x-2 mt-6">
                    <Button
                        variant="outline"
                        @click="isDeleteModalOpen = false"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        @click="confirmDelete"
                    >
                        Delete
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script>
import { ref } from 'vue'
import moment from 'moment'

import {
    PlusIcon,
    Loader2Icon,
    MessageCircleIcon,
    MoreVerticalIcon,
    EditIcon,
    TrashIcon,
} from 'lucide-vue-next'

import { Button } from '@/components/ui/button'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export default {
    name: 'ConversationHistory',

    components: {
        Button,
        PlusIcon,
        Loader2Icon,
        MessageCircleIcon,
        MoreVerticalIcon,
        EditIcon,
        TrashIcon,
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogDescription,
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
    },

    props: {
        conversations: {
            type: Array,
            default: () => []
        },
        currentConversationId: {
            type: String,
            default: null
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        hasMore: {
            type: Boolean,
            default: false
        },
        isLoadingMore: {
            type: Boolean,
            default: false
        }
    },

    emits: [
        'select-conversation',
        'new-conversation',
        'rename-conversation',
        'delete-conversation',
        'load-more'
    ],

    setup(props, { emit }) {
        const listContainer = ref(null)
        const isRenameModalOpen = ref(false)
        const isDeleteModalOpen = ref(false)
        const selectedConversation = ref(null)
        const newTitle = ref('')

        const formatDate = (dateString) => {
            const date = moment(dateString)
            const now = moment()

            if (date.isSame(now, 'day')) {
                return date.format('h:mm A')
            } if (date.isSame(now.clone().subtract(1, 'day'), 'day')) {
                return 'Yesterday'
            } if (date.isAfter(now.clone().subtract(7, 'days'))) {
                return date.format('ddd')
            }
                return date.format('MMM D')
        }

        const getMessagePreview = (message) => {
            if (!message || !message.content) return ''
            const content = message.content.trim()
            return content.length > 50 ? `${content.substring(0, 50)  }...` : content
        }

        const handleRename = (conversation) => {
            selectedConversation.value = conversation
            newTitle.value = conversation.title || ''
            isRenameModalOpen.value = true
        }

        const handleDelete = (conversation) => {
            selectedConversation.value = conversation
            isDeleteModalOpen.value = true
        }

        const confirmDelete = () => {
            if (selectedConversation.value) {
                emit('delete-conversation', selectedConversation.value._id)
                isDeleteModalOpen.value = false
                selectedConversation.value = null
            }
        }

        const confirmRename = () => {
            if (newTitle.value.trim() && selectedConversation.value) {
                emit('rename-conversation', selectedConversation.value._id, newTitle.value.trim())
                isRenameModalOpen.value = false
                newTitle.value = ''
            }
        }

        const handleScroll = (event) => {
            if (!props.hasMore || props.isLoadingMore) return

            const target = event.target
            if (!target) return

            const threshold = 120
            const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold

            if (isNearBottom) emit('load-more')
        }

        return {
            listContainer,
            isRenameModalOpen,
            isDeleteModalOpen,
            selectedConversation,
            newTitle,
            formatDate,
            getMessagePreview,
            handleRename,
            handleDelete,
            confirmRename,
            confirmDelete,
            handleScroll,
        }
    },
}

</script>